import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Banknote, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/contexts/AuthContext";
import { InvoiceAPI, PaymentAPI, extractError } from "@/lib/api";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;
const METHODS = ["cash", "upi", "cheque", "bank_transfer"];

const useTitle = (label) => {
    useEffect(() => {
        document.title = `AutoBrix · ${label}`;
        return () => { document.title = "AutoBrix"; };
    }, [label]);
};

export default function PaymentsPage() {
    useTitle("Payments");
    const { branches, permissions } = useAuth();
    const [items, setItems] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const canCreate = permissions.includes("payment:create");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await PaymentAPI.list({ branch_id: branchId || undefined, page_size: 200 });
            setItems(d.items || []);
        } finally { setLoading(false); }
    }, [branchId]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="payments-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Receipts & settlements</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Payments</h1>
                    <p className="mt-1 text-sm text-zinc-500">Manual cash/UPI/cheque/bank entries plus gateway captures. Idempotent on (provider, event_id).</p>
                </div>
                {canCreate && (
                    <PaymentNewDialog open={newOpen} onOpenChange={setNewOpen} onCreated={() => { setNewOpen(false); load(); }} />
                )}
            </header>

            <div className="flex flex-wrap items-center gap-2">
                <Select value={branchId} onValueChange={(v) => setBranchId(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="payments-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All branches</SelectItem>
                        {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <span className="font-mono text-xs text-zinc-500" data-testid="payments-total-count">{loading ? "…" : `${items.length} payments`}</span>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="payments-table">
                    <TableHeader><TableRow>
                        <TableHead>Payment #</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Received</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {items.map((p) => (
                            <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                                <TableCell className="font-mono">{p.payment_number || "—"}</TableCell>
                                <TableCell><Link to={`/invoices/${p.invoice_id}`} className="text-brand-redDeep hover:underline dark:text-brand-red font-mono"><ArrowRight className="inline h-3 w-3" /> {p.invoice_number || p.invoice_id?.slice(-6)}</Link></TableCell>
                                <TableCell>{p.method?.replace("_", " ")}</TableCell>
                                <TableCell>{p.status}</TableCell>
                                <TableCell className="text-right font-mono">{inr(p.amount_minor)}</TableCell>
                                <TableCell className="text-xs font-mono text-zinc-500">{p.received_at ? new Date(p.received_at).toLocaleString() : "—"}</TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-zinc-500">No payments yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function PaymentNewDialog({ open, onOpenChange, onCreated }) {
    const { branches, currentBranchId } = useAuth();
    const [branchId, setBranchId] = useState(currentBranchId || "");
    const [invoiceQ, setInvoiceQ] = useState("");
    const [invoices, setInvoices] = useState([]);
    const [invoice, setInvoice] = useState(null);
    const [amount, setAmount] = useState(0);
    const [method, setMethod] = useState("cash");
    const [reference, setReference] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        InvoiceAPI.list({
            branch_id: branchId || undefined,
            status: "issued",
            page_size: 50,
        }).then((d) => setInvoices(d.items || []));
    }, [branchId, open]);

    const pick = (inv) => {
        setInvoice(inv);
        setAmount(inv.amount_due_minor);
    };
    const submit = async () => {
        if (!invoice || !amount || amount <= 0) { toast.error("Pick an invoice and an amount"); return; }
        setBusy(true);
        try {
            const r = await PaymentAPI.create({
                invoice_id: invoice.id, branch_id: invoice.branch_id,
                amount_minor: Number(amount), method, reference: reference || null, notes: notes || null,
            });
            toast.success(`Recorded ${r.payment_number}`);
            onCreated();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    };

    const filtered = (invoices || []).filter((i) =>
        (i.invoice_number || "").toLowerCase().includes(invoiceQ.toLowerCase()) ||
        (i.customer_snapshot?.display_name || "").toLowerCase().includes(invoiceQ.toLowerCase())
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="payment-new-btn">
                    <Plus className="mr-1 h-4 w-4" />Record payment
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="payment-new-dialog">
                <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Branch</Label>
                        <Select value={branchId} onValueChange={setBranchId}>
                            <SelectTrigger data-testid="payment-new-branch-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Find invoice</Label>
                        <Input value={invoiceQ} onChange={(e) => setInvoiceQ(e.target.value)}
                               placeholder="Type invoice number or customer name"
                               data-testid="payment-invoice-search" />
                        <div className="max-h-40 overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                            {filtered.slice(0, 12).map((i) => (
                                <button type="button" key={i.id}
                                        onClick={() => pick(i)}
                                        data-testid={`payment-invoice-option-${i.id}`}
                                        className={`block w-full px-3 py-1.5 text-left text-xs font-mono hover:bg-zinc-50 dark:hover:bg-zinc-900 ${invoice?.id === i.id ? "bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/10 dark:text-brand-red" : ""}`}>
                                    {i.invoice_number} · {i.customer_snapshot?.display_name} · due {inr(i.amount_due_minor)}
                                </button>
                            ))}
                            {filtered.length === 0 && <div className="px-3 py-3 text-xs text-zinc-500">No issued invoices match.</div>}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>Method</Label>
                            <Select value={method} onValueChange={setMethod}>
                                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Amount (paise)</Label>
                            <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="payment-amount-input" />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label>Reference</Label>
                        <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="payment-reference-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Notes</Label>
                        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="payment-notes-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !invoice} className="bg-brand-red text-white hover:bg-brand-redDeep"
                            data-testid="payment-submit">
                        <Banknote className="mr-1 h-4 w-4" />Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
